import { useCallback, useEffect, useRef, useState } from 'react';
import { Descendant } from 'slate';

import { getDocument, getDocumentHistory } from '../../store/investor/investorApiHelper';
import { sanitizeSlateValue } from '../TextEditor/utils/SlateSerialisationUtil';
import { unwrapDocVersion, runMigrations } from '../PlateEditor/migrations';
import { HistoryEntry } from './HistorySideDrawer';
import { useHistoryDrawer } from './useHistoryDrawer';

// Restore-after-save timings — mirror DealSummaryEditor's tuned values.
const RESTORE_PERSIST_DELAY = 500;
const RESTORE_RELOAD_DELAY = 300;

export interface UseDocumentHistoryConfig {
    documentType: string;
    assesseeOrgId: string;
    /**
     * Lineage id getter. Read at fetch time (not hook init) because the genId is
     * latched lazily — the backend mints it on the document's first save.
     * History is effectively empty until a lineage exists.
     */
    getGenId: () => string | undefined;
    /** Live editor content ref. Hook reads from here and writes restored versions to it. */
    editorContentRef: React.MutableRefObject<Descendant[]>;
    /** Push sanitized template data into the editor (e.g. setEditorInitialData / setCurrentEditorData). */
    onLoadVersion: (sanitizedData: Descendant[]) => void;
    /** Persist content as a new committed version. Resolves when backend has saved. */
    persistVersion: (content: Descendant[]) => Promise<void>;
    /** Disable history features (no fetch, no auto-save, drawer never opens). */
    enabled?: boolean;
    /** Optional: invoked once when the drawer is opened (e.g. fire Mixpanel "view history" event). */
    onHistoryViewed?: () => void;
    /**
     * Invoked synchronously, before the pre-flight save fires, whenever the
     * drawer is about to open with unsaved changes. Lets the consumer cancel
     * its own already-armed debounced autosave timer so it can't fire a
     * second, competing write for the same content right after this save
     * lands (both would resolve to the same server row, but concurrently).
     */
    onBeforeHistoryPreflightSave?: () => void;
}

export interface UseDocumentHistoryReturn {
    /** Bump this on the editor's `key` prop to force a remount when a historical version is loaded. */
    editorKey: number;
    selectedHistoryEntryId: string | null;
    isOpen: boolean;
    isExpanded: boolean;
    loading: boolean;
    previewLoading: boolean;
    historyEntries: HistoryEntry[];
    closeDrawer: () => void;
    toggleExpand: () => void;
    selectHistoryEntry: (entry: HistoryEntry) => Promise<void>;
    previewHistoryEntry: (entry: HistoryEntry) => Promise<void>;
    handleToggleHistory: () => Promise<void>;
    hasUnsavedChanges: () => boolean;
    /** Call this after a non-history save succeeds so the unsaved-changes check stays accurate. */
    markAsSaved: (content: Descendant[]) => void;
}

/**
 * Shared history-drawer integration for rich-text editors (OnePager, Application Form, etc.).
 *
 * Encapsulates: auto-save before drawer open, version load / preview / restore, editor remount via
 * key bump, "last saved" snapshot tracking, and history-list reload after restore.
 *
 * Versions live in the unified document engine: the list is the genId lineage's
 * committed versions (getDocumentHistory) and each version is its own document
 * row (getDocument by id). Each consumer plugs in its own editor state setter
 * (`onLoadVersion`) and save function (`persistVersion`) — the rest of the
 * workflow is owned by the hook.
 */
export function useDocumentHistory({
    documentType,
    assesseeOrgId,
    getGenId,
    editorContentRef,
    onLoadVersion,
    persistVersion,
    enabled = true,
    onHistoryViewed,
    onBeforeHistoryPreflightSave,
}: UseDocumentHistoryConfig): UseDocumentHistoryReturn {
    const [editorKey, setEditorKey] = useState(0);
    const [selectedHistoryEntryId, setSelectedHistoryEntryId] = useState<string | null>(null);
    const reloadHistoryRef = useRef<(() => Promise<void>) | null>(null);
    const lastSavedDataRef = useRef<Descendant[] | null>(null);
    // True from the moment a restore starts until its save + history re-select
    // finishes. Guards onDrawerClose from reverting the editor to "latest"
    // (a preview-only behaviour) and undoing the restore mid-flight.
    const restoreInFlightRef = useRef(false);

    const hasUnsavedChanges = useCallback(() => {
        if (!lastSavedDataRef.current || !editorContentRef.current) return true;
        return JSON.stringify(lastSavedDataRef.current) !== JSON.stringify(editorContentRef.current);
    }, [editorContentRef]);

    const save = async () => {
        if (!editorContentRef.current?.length) return;
        try {
            // Pre-flight before opening the history side drawer: commit the current
            // content so the list's newest entry matches what the editor shows.
            await persistVersion(editorContentRef.current);
            lastSavedDataRef.current = editorContentRef.current;
        } catch (err) {
            console.error('Error saving template:', err);
        }
    };

    const fetchHistoryEntries = useCallback(async (): Promise<HistoryEntry[]> => {
        const genId = getGenId();
        if (!enabled || !genId) return [];
        const versions = await getDocumentHistory({ assesseeOrgId, genId });
        return versions.map(
            (v): HistoryEntry => ({
                _id: String(v.id ?? v._id ?? ''),
                // Backend (getVersionHistory) emits a single lastEditedBy key,
                // already lastUpdatedBy→createdBy fallback'd server-side.
                userName: v.lastEditedBy ?? v.createdBy ?? '',
                lastUpdatedAt: v.lastUpdatedAt ?? v.createdAt ?? 0,
                isRestored: false,
            }),
        );
    }, [enabled, assesseeOrgId, getGenId]);

    // Each history version is its own document row; unwrap its content envelope
    // the same way the initial load does (version + migrations before sanitize).
    const loadVersionNodes = useCallback(
        async (docId: string): Promise<Descendant[]> => {
            const doc = await getDocument({ assesseeOrgId, docId });
            const { version, value } = unwrapDocVersion(doc?.content ?? []);
            return sanitizeSlateValue(runMigrations(version, value));
        },
        [assesseeOrgId],
    );

    const historyDrawer = useHistoryDrawer({
        documentType,
        fetchHistoryFn: fetchHistoryEntries,
        fetchVersionFn: (docId: string) => loadVersionNodes(docId),
        onVersionLoaded: (sanitizedData, shouldSave, entryId, _isInitialLoad = false) => {
            onLoadVersion(sanitizedData);
            editorContentRef.current = sanitizedData;
            setEditorKey((prev) => prev + 1);
            setSelectedHistoryEntryId(entryId || null);

            // Track as saved data since it's loaded from backend
            if (!shouldSave) {
                lastSavedDataRef.current = sanitizedData;
            }

            if (shouldSave) {
                // Restore flow: persist as a new version, then refresh the history list and re-select the new current.
                restoreInFlightRef.current = true;
                setTimeout(async () => {
                    try {
                        await persistVersion(sanitizedData);
                        lastSavedDataRef.current = sanitizedData;
                        if (reloadHistoryRef.current) {
                            try {
                                await reloadHistoryRef.current();
                            } catch (e) {
                                // already toasted inside hook
                            }
                        }
                        setTimeout(async () => {
                            try {
                                const entries = await fetchHistoryEntries();
                                const sortedEntries = (entries || []).sort(
                                    (a: any, b: any) => (b?.lastUpdatedAt || 0) - (a?.lastUpdatedAt || 0),
                                );
                                if (sortedEntries.length > 0) {
                                    setSelectedHistoryEntryId(sortedEntries[0]._id);
                                }
                            } catch (error) {
                                console.error('Error fetching updated history:', error);
                            } finally {
                                restoreInFlightRef.current = false;
                            }
                        }, RESTORE_RELOAD_DELAY);
                    } catch (err) {
                        console.error('Restore save failed:', err);
                        restoreInFlightRef.current = false;
                    }
                }, RESTORE_PERSIST_DELAY);
            }
        },
        onDrawerClose: () => {
            // If user closed the drawer while PREVIEWING an older version, reload
            // the latest. Skip entirely while a restore is in flight — the editor
            // already holds the restored content and reverting would undo it.
            if (!restoreInFlightRef.current && selectedHistoryEntryId && historyDrawer?.historyEntries?.length) {
                const selectedIndex = historyDrawer.historyEntries.findIndex((e) => e._id === selectedHistoryEntryId);
                if (selectedIndex > 0) {
                    const currentVersion = historyDrawer.historyEntries[0];
                    if (currentVersion) {
                        loadVersionNodes(currentVersion._id)
                            .then((sanitizedData) => {
                                onLoadVersion(sanitizedData);
                                editorContentRef.current = sanitizedData;
                                setEditorKey((prev) => prev + 1);
                                lastSavedDataRef.current = sanitizedData;
                            })
                            .catch((error) => console.error('Error reloading current version:', error));
                    }
                }
            }
            setSelectedHistoryEntryId(null);
        },
    });

    useEffect(() => {
        reloadHistoryRef.current = historyDrawer.reloadHistory;
    }, [historyDrawer.reloadHistory]);

    const handleToggleHistory = useCallback(async () => {
        if (!enabled) return;
        const willOpen = !historyDrawer.isOpen;
        // Toggle the drawer FIRST so the slide-in animation starts immediately.
        // Previously this happened AFTER awaiting save() + a 300ms setTimeout —
        // result: ~1s of dead air between clicking the button and seeing the
        // drawer move. The pre-flight save (if any) now runs in parallel with
        // the animation, and reloadHistory will pick up the new version once
        // the save resolves.
        historyDrawer.toggleDrawer();
        if (willOpen) {
            onHistoryViewed?.();
            if (hasUnsavedChanges()) {
                // Cancel the consumer's own armed autosave timer FIRST — otherwise it
                // can fire while (or right after) this pre-flight save is in flight,
                // writing the same content again as a second, racing save.
                onBeforeHistoryPreflightSave?.();
                // Fire and forget; refresh the list once the save lands.
                save()
                    .then(() => reloadHistoryRef.current?.())
                    .catch((err) => console.error('history pre-flight save failed', err));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, historyDrawer.isOpen, hasUnsavedChanges, onHistoryViewed, onBeforeHistoryPreflightSave]);

    const markAsSaved = useCallback((content: Descendant[]) => {
        lastSavedDataRef.current = content;
    }, []);

    return {
        editorKey,
        selectedHistoryEntryId,
        isOpen: historyDrawer.isOpen,
        isExpanded: historyDrawer.isExpanded,
        loading: historyDrawer.loading,
        previewLoading: historyDrawer.previewLoading,
        historyEntries: historyDrawer.historyEntries,
        closeDrawer: historyDrawer.closeDrawer,
        toggleExpand: historyDrawer.toggleExpand,
        selectHistoryEntry: historyDrawer.selectHistoryEntry,
        previewHistoryEntry: historyDrawer.previewHistoryEntry,
        handleToggleHistory,
        hasUnsavedChanges,
        markAsSaved,
    };
}
